import {makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import {ViewController} from "data/types/structure";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {ChangeEvent, SyntheticEvent} from "react";
import type {IUserStore} from "data/stores/user/user.store";
import {RequestState} from "data/enums";
import {extractErrorMessage} from "data/utils";
import type {AxiosError} from "axios";
import type {IApiResponse} from "data/services/http";

export interface IOffSeasonFormController extends ViewController {
	readonly i18n: ILocalizationStore;

	get error(): string;
	get email(): string;
	get isFormDisabled(): boolean;
	get isSubmitted(): boolean;

	handleSubmit: (event: SyntheticEvent) => void;
	handleChange: (event: ChangeEvent<HTMLFormElement>) => void;
}

@injectable()
export class OffSeasonFormController implements IOffSeasonFormController {
	@observable private _requestState = RequestState.IDLE;
	@observable private _error: string = "";
	@observable private _email: string = "";

	get error() {
		return this._error;
	}

	get email() {
		return this._email;
	}

	get isLoading() {
		return this._requestState === RequestState.PENDING;
	}

	get isSubmitted() {
		return this._requestState === RequestState.SUCCESS;
	}

	get isFormDisabled() {
		return this.isLoading || this.isSubmitted;
	}

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore
	) {
		makeAutoObservable(this);
	}

	handleSubmit = async (e: SyntheticEvent) => {
		e.preventDefault();

		try {
			this._requestState = RequestState.PENDING;
			await this._userStore.welcomeBackEmail(this._email);

			runInAction(() => {
				this._requestState = RequestState.SUCCESS;
			});
		} catch (e) {
			this._error = extractErrorMessage(e as AxiosError<IApiResponse>);

			runInAction(() => {
				this._requestState = RequestState.IDLE;
			});
		}
	};

	handleChange = (e: ChangeEvent<HTMLFormElement>) => {
		e.preventDefault();
		this._error = "";
		this._email = e.target.value as string;
	};
}
